import { useState, useEffect, useRef } from 'react';
import { GlueClient, GetTableCommand } from "@aws-sdk/client-glue";
import { useAuth } from '../context/auth-provider';

interface IuseGlueResult {
  getTableDetails: (dbName: string, tableName: string) => Promise<IGlueCatalogTable | null>;
}

export const useGlueClient = () : IuseGlueResult => {

  const { user } = useAuth();
  const glueClientRef = useRef<any | null>();
  const [glueError, setGlueError] = useState<string | null>(null);

  useEffect(() => {

    try {

      if (!user || !user.accessKeyId || !user.secretAccessKey || !user.sessionToken) return;

      glueClientRef.current = new GlueClient({
        region: process.env.REACT_APP_AWS_REGION!,
        credentials: {
          accessKeyId: user.accessKeyId,
          secretAccessKey: user.secretAccessKey,
          sessionToken: user.sessionToken }});
      
    } catch(e) {
      console.error(`useGlueClient.useEffect :: error: ${e}`);
    }
  }, [user]);

  const getTableDetails = async (dbName: string, tableName: string) : Promise<IGlueCatalogTable | null> =>  {

    if (!glueClientRef.current) return null;

    try {

      // console.log(`useGlueClient.getTableDetails :: dbName: ${dbName}, tableName: ${tableName}`);

      // Clear any pre-existing errors
      setGlueError(null);

      // API call to get table details
      const data = await glueClientRef.current.send(
        new GetTableCommand({
          DatabaseName: dbName,
          Name: tableName }));

      // Parse data and return IGlueCatalogTable
      return parseTableDetails(data);

    } catch (err) {
      setGlueError(`There was an error accessing Glue catalog table: ${dbName}/${tableName}`);
      return null;
    }
  };

  return { getTableDetails };
};

const parseTableDetails = (data: any) : IGlueCatalogTable => {

  const tableDetails = {} as IGlueCatalogTable;  

  if (data?.Table?.Name) tableDetails.tableName = data.Table.Name;
  if (data?.Table?.DatabaseName) tableDetails.databaseName = data.Table.DatabaseName;
  if (data?.Table?.StorageDescriptor?.Parameters?.UPDATED_BY_CRAWLER) tableDetails.crawlerName = data.Table.StorageDescriptor.Parameters.UPDATED_BY_CRAWLER;
  if (data?.Table?.CreateTime) tableDetails.createTime = data.Table.CreateTime;
  if (data?.Table?.StorageDescriptor?.Location) tableDetails.location = data.Table.StorageDescriptor.Location;
  if (data?.Table?.StorageDescriptor?.SerdeInfo?.SerializationLibrary) tableDetails.serializationLibrary = data.Table.StorageDescriptor.SerdeInfo.SerializationLibrary;
  if (data?.Table?.StorageDescriptor?.Parameters?.classification) tableDetails.classification = data.Table.StorageDescriptor.Parameters.classification;
  if (data?.Table?.StorageDescriptor?.Parameters?.delimiter) tableDetails.delimiter = data.Table.StorageDescriptor.Parameters.delimiter;
  if (data?.Table?.StorageDescriptor?.Parameters?.recordCount) tableDetails.recordCount = data.Table.StorageDescriptor.Parameters.recordCount;

  if (data?.Table?.StorageDescriptor?.Columns) {
    tableDetails.columns = data.Table.StorageDescriptor.Columns.map((col: any) => {
      return { name: col.Name, type: col.Type } as IGlueCatalogColumn
    });
  }

  return tableDetails
};